import { useState } from "react";
import SpotPrice from "@/components/SpotPrice";

const fm = (n, d = 0) => n.toLocaleString("da-DK", { minimumFractionDigits: d, maximumFractionDigits: d });
const num = (s) => parseFloat(s) || 0;

function Digits({ value, redLast = false, testid }) {
  let s = String(Math.min(999999, Math.round(value)));
  while (s.length < 6) s = "0" + s;
  return (
    <div className="dgs" aria-hidden="true" data-testid={testid}>
      {s.split("").map((c, i) => (
        <span key={i} className={"dg" + (redLast && i === 5 ? " r" : "")}>
          {c}
        </span>
      ))}
    </div>
  );
}

const PRESETS = [
  [null, null],
  [8, 5],
  [100, 4],
  [200, 6],
  [2000, 0.3],
  [1500, 6],
  [2500, 1],
];
const PRESET_NAMES = ["Eget apparat", "LED-pære", "Fladskærms-tv", "Stationær computer", "Elkedel", "Elradiator", "Tørretumbler"];

export function ApparatCalc() {
  const [pre, setPre] = useState("0");
  const [w, setW] = useState("100");
  const [h, setH] = useState("4");
  const [p, setP] = useState("3");
  const wn = num(w), hn = num(h), pn = num(p);
  const kwh = ((wn * hn) / 1000) * 365;
  const day = ((wn * hn) / 1000) * pn;

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Beregn hvad et apparat koster i strøm">
      <div className="mrow">
        <label htmlFor="ac-pre">Apparat</label>
        <select
          id="ac-pre"
          data-testid="calc-apparat-select"
          value={pre}
          onChange={(e) => {
            const v = e.target.value;
            setPre(v);
            const pr = PRESETS[+v];
            if (pr[0] !== null) {
              setW(String(pr[0]));
              setH(String(pr[1]));
            }
          }}
        >
          {PRESET_NAMES.map((n, i) => (
            <option key={i} value={String(i)}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div className="mgrid">
        <div>
          <label htmlFor="ac-w">Effekt (watt)</label>
          <input
            id="ac-w"
            data-testid="calc-apparat-watt-input"
            type="number"
            inputMode="decimal"
            min="0"
            value={w}
            onChange={(e) => {
              setW(e.target.value);
              setPre("0");
            }}
          />
        </div>
        <div>
          <label htmlFor="ac-h">Timer pr. dag</label>
          <input
            id="ac-h"
            data-testid="calc-apparat-hours-input"
            type="number"
            inputMode="decimal"
            min="0"
            max="24"
            step="0.1"
            value={h}
            onChange={(e) => {
              setH(e.target.value);
              setPre("0");
            }}
          />
        </div>
        <div>
          <label htmlFor="ac-p">Pris pr. kWh (kr.)</label>
          <input
            id="ac-p"
            data-testid="calc-apparat-price-input"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={p}
            onChange={(e) => setP(e.target.value)}
          />
        </div>
      </div>
      <div className="read">
        <Digits value={kwh * 10} redLast testid="calc-apparat-meter-digits" />
        <div className="cap">kWh pr. år</div>
      </div>
      <dl className="stats" aria-live="polite">
        <div>
          <dt>Pr. dag</dt>
          <dd data-testid="calc-apparat-day-output">{fm(day, 2)} kr.</dd>
        </div>
        <div>
          <dt>Pr. måned</dt>
          <dd data-testid="calc-apparat-month-output">{fm((day * 365) / 12, 0)} kr.</dd>
        </div>
        <div className="big">
          <dt>Pr. år</dt>
          <dd data-testid="calc-apparat-year-output">{fm(day * 365, 0)} kr.</dd>
        </div>
      </dl>
      <SpotPrice onUsePrice={(v) => setP(String(v))} />
      <p className="fine">
        Typiske værdier. Tjek mærkepladen på dit apparat, og brug din samlede pris inkl. tariffer og afgifter.
      </p>
    </form>
  );
}

export function BenzinCalc() {
  const [v, setV] = useState({ km: "15000", mp: "0", ox: "0", kl: "15", bp: "19.87", fk: "17", lt: "10", ph: "1.50", po: "3.50", sh: "10" });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const km = num(v.km), mp = num(v.mp), ox = num(v.ox), kl = num(v.kl), bp = num(v.bp), fk = num(v.fk), lt = num(v.lt), ph = num(v.ph), po = num(v.po);
  const sh = Math.min(100, Math.max(0, num(v.sh))) / 100;
  const ben = kl > 0 ? (km / kl) * bp : 0;
  const kwh = (km / 100) * fk * (1 + lt / 100);
  const el = kwh * ((1 - sh) * ph + sh * po) + ox;
  const save = ben - el;
  let be = "–";
  if (mp > 0) be = save > 0 ? (mp / save > 50 ? "Over 50 år" : fm(mp / save, 1) + " år") : "Aldrig";

  const F = (id, label, k, extra = {}) => (
    <div>
      <label htmlFor={"bz-" + id}>{label}</label>
      <input id={"bz-" + id} data-testid={"calc-benzin-" + id + "-input"} type="number" inputMode="decimal" min="0" value={v[k]} onChange={set(k)} {...extra} />
    </div>
  );

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Sammenlign elbil og benzinbil">
      <div className="mgrid">
        {F("km", "Km pr. år", "km")}
        {F("mp", "Merpris elbil (kr.)", "mp")}
        <div>
          <label htmlFor="bz-ox">Andre merudgifter elbil pr. år (kr.)</label>
          <input id="bz-ox" data-testid="calc-benzin-ox-input" type="number" inputMode="decimal" value={v.ox} onChange={set("ox")} />
        </div>
      </div>
      <p className="grp">Benzinbil</p>
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {F("kl", "Km pr. liter", "kl", { step: "0.1" })}
        {F("bp", "Benzinpris (kr./l)", "bp", { step: "0.01" })}
      </div>
      <p className="grp">Elbil</p>
      <div className="mgrid">
        {F("fk", "Forbrug (kWh/100 km)", "fk", { step: "0.1" })}
        {F("lt", "Ladetab (%)", "lt", { max: "50" })}
        {F("ph", "Pris hjemme (kr./kWh)", "ph", { step: "0.01" })}
      </div>
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: "10px" }}>
        {F("po", "Pris offentligt (kr./kWh)", "po", { step: "0.01" })}
        {F("sh", "Offentligt (% af ladning)", "sh", { max: "100" })}
      </div>
      <dl className="stats" aria-live="polite" style={{ marginTop: "20px" }}>
        <div>
          <dt>Benzin pr. år</dt>
          <dd data-testid="calc-benzin-year-output">{fm(ben, 0)} kr.</dd>
        </div>
        <div>
          <dt>Elbil pr. år</dt>
          <dd data-testid="calc-benzin-el-output">{fm(el, 0)} kr.</dd>
        </div>
        <div className="big">
          <dt>Besparelse pr. år</dt>
          <dd data-testid="calc-benzin-save-output">{fm(save, 0)} kr.</dd>
        </div>
      </dl>
      <dl className="stats" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
        <div>
          <dt>Benzin pr. 100 km</dt>
          <dd data-testid="calc-benzin-b100-output">{km > 0 ? fm((ben / km) * 100, 0) + " kr." : "–"}</dd>
        </div>
        <div>
          <dt>Elbil pr. 100 km</dt>
          <dd data-testid="calc-benzin-e100-output">{km > 0 ? fm((el / km) * 100, 0) + " kr." : "–"}</dd>
        </div>
        <div>
          <dt>Merpris tjent hjem på</dt>
          <dd data-testid="calc-benzin-breakeven-output">{be}</dd>
        </div>
      </dl>
      <p className="fine">
        Eksempelværdier. Benzinprisen er fra fuel-prices.eu den 12. oktober 2026. Andre merudgifter lægges til elbilen
        (skriv minus, hvis elbilen er billigere). Ret alt til dine egne tal.
      </p>
    </form>
  );
}

export function ElbilCalc() {
  const [v, setV] = useState({ km: "15000", fk: "17", lt: "10", ph: "1.50", po: "3.50", sh: "10" });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const km = num(v.km), fk = num(v.fk), lt = num(v.lt), ph = num(v.ph), po = num(v.po);
  const sh = Math.min(100, Math.max(0, num(v.sh))) / 100;
  const kwh = (km / 100) * fk * (1 + lt / 100);
  const cost = kwh * ((1 - sh) * ph + sh * po);

  const F = (id, label, k, extra = {}) => (
    <div>
      <label htmlFor={"eb-" + id}>{label}</label>
      <input id={"eb-" + id} data-testid={"calc-elbil-" + id + "-input"} type="number" inputMode="decimal" min="0" value={v[k]} onChange={set(k)} {...extra} />
    </div>
  );

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Elbil-beregner">
      <div className="mgrid">
        {F("km", "Km pr. år", "km")}
        {F("fk", "Forbrug (kWh/100 km)", "fk", { step: "0.1" })}
        {F("lt", "Ladetab (%)", "lt", { max: "50" })}
      </div>
      <div className="mgrid" style={{ marginTop: "10px" }}>
        {F("ph", "Pris hjemme (kr./kWh)", "ph", { step: "0.01" })}
        {F("po", "Pris offentligt (kr./kWh)", "po", { step: "0.01" })}
        {F("sh", "Offentligt (% af ladning)", "sh", { max: "100" })}
      </div>
      <div className="read">
        <Digits value={kwh} testid="calc-elbil-meter-digits" />
        <div className="cap">kWh fra nettet pr. år</div>
      </div>
      <dl className="stats" aria-live="polite">
        <div>
          <dt>Pr. måned</dt>
          <dd data-testid="calc-elbil-month-output">{fm(cost / 12, 0)} kr.</dd>
        </div>
        <div>
          <dt>Pr. 100 km</dt>
          <dd data-testid="calc-elbil-per100-output">{km > 0 ? fm((cost / km) * 100, 1) + " kr." : "–"}</dd>
        </div>
        <div className="big">
          <dt>Pr. år</dt>
          <dd data-testid="calc-elbil-year-output">{fm(cost, 0)} kr.</dd>
        </div>
      </dl>
      <p className="fine">
        Eksempelværdier. Brug din egen samlede pris inkl. tarif og afgifter. Ladetabet er regnet på al opladning.
      </p>
    </form>
  );
}

export function SolcelleCalc() {
  const [v, setV] = useState({ m2: "30", pris: "70000", kr: "3", egen: "30", salg: "0.70" });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const m2 = num(v.m2), pris = num(v.pris), kr = num(v.kr), salg = num(v.salg);
  const egen = Math.min(100, Math.max(0, num(v.egen))) / 100;
  const kwp = m2 * 0.2;
  const prod = kwp * 950;
  const vaerdi = prod * (egen * kr + (1 - egen) * salg);
  const aar = vaerdi > 0 ? pris / vaerdi : 0;
  const aarTxt = pris <= 0 ? "–" : vaerdi <= 0 ? "Aldrig" : aar > 30 ? "Over 30 år" : fm(aar, 1) + " år";

  const F = (id, label, k, extra = {}) => (
    <div>
      <label htmlFor={"sc-" + id}>{label}</label>
      <input id={"sc-" + id} data-testid={"calc-solceller-" + id + "-input"} type="number" inputMode="decimal" min="0" value={v[k]} onChange={set(k)} {...extra} />
    </div>
  );

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Solcelle-beregner">
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {F("m2", "Tagareal til paneler (m²)", "m2")}
        {F("pris", "Anlægspris (kr.)", "pris")}
      </div>
      <div className="mgrid" style={{ marginTop: "10px" }}>
        {F("kr", "Din elpris (kr./kWh)", "kr", { step: "0.01" })}
        {F("egen", "Egenforbrug (%)", "egen", { max: "100" })}
        {F("salg", "Salgspris overskud (kr./kWh)", "salg", { step: "0.01" })}
      </div>
      <div className="read">
        <Digits value={prod} testid="calc-solceller-meter-digits" />
        <div className="cap">kWh produceret pr. år</div>
      </div>
      <dl className="stats" aria-live="polite">
        <div>
          <dt>Anlæg</dt>
          <dd data-testid="calc-solceller-size-output">{fm(kwp, 1)} kWp</dd>
        </div>
        <div>
          <dt>Besparelse pr. år</dt>
          <dd data-testid="calc-solceller-year-output">{fm(vaerdi, 0)} kr.</dd>
        </div>
        <div className="big">
          <dt>Tilbagebetaling</dt>
          <dd data-testid="calc-solceller-payback-output">{aarTxt}</dd>
        </div>
      </dl>
      <p className="fine">
        Tommelfingerregler: 200 watt pr. m² tag og ca. 950 kWh pr. kWp om året i Danmark. Egenforbruget er den del af
        strømmen, I selv når at bruge. Uden batteri og serviceudgifter.
      </p>
    </form>
  );
}

const LABEL_KWH = { A2020: 25, A2015: 35, B: 47, C: 62, D: 90, E: 130, F: 175, G: 225 };
const LABEL_ORDER = ["A2020", "A2015", "B", "C", "D", "E", "F", "G"];

export function EnergimaerkeCalc() {
  const [m2, setM2] = useState("140");
  const [from, setFrom] = useState("E");
  const [to, setTo] = useState("B");
  const [pris, setPris] = useState("1.00");
  const area = num(m2), p = num(pris);
  const diff = Math.max(0, LABEL_KWH[from] - LABEL_KWH[to]);
  const kwh = diff * area;
  const kr = kwh * p;

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Energimærke-beregner">
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <label htmlFor="em-m2">Boligens størrelse (m²)</label>
          <input id="em-m2" data-testid="calc-label-m2-input" type="number" inputMode="decimal" min="0" value={m2} onChange={(e) => setM2(e.target.value)} />
        </div>
        <div>
          <label htmlFor="em-pris">Varmepris (kr./kWh)</label>
          <input id="em-pris" data-testid="calc-label-price-input" type="number" inputMode="decimal" min="0" step="0.01" value={pris} onChange={(e) => setPris(e.target.value)} />
        </div>
      </div>
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: "10px" }}>
        <div>
          <label htmlFor="em-from">Nuværende mærke</label>
          <select id="em-from" data-testid="calc-label-from-select" value={from} onChange={(e) => setFrom(e.target.value)}>
            {LABEL_ORDER.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="em-to">Ønsket mærke</label>
          <select id="em-to" data-testid="calc-label-to-select" value={to} onChange={(e) => setTo(e.target.value)}>
            {LABEL_ORDER.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="read">
        <Digits value={kwh} testid="calc-label-meter-digits" />
        <div className="cap">kWh sparet pr. år</div>
      </div>
      <dl className="stats" aria-live="polite">
        <div>
          <dt>Pr. måned</dt>
          <dd data-testid="calc-label-month-output">{fm(kr / 12, 0)} kr.</dd>
        </div>
        <div>
          <dt>Pr. m²</dt>
          <dd data-testid="calc-label-m2-output">{area > 0 ? fm(kr / area, 0) + " kr." : "–"}</dd>
        </div>
        <div className="big">
          <dt>Pr. år</dt>
          <dd data-testid="calc-label-year-output">{fm(kr, 0)} kr.</dd>
        </div>
      </dl>
      <p className="fine">
        Vi bruger midtpunkter af Energistyrelsens intervaller pr. bogstav. Det faktiske forbrug afhænger af jeres vaner
        og varmesystem. Vælg et bedre mærke end det nuværende for at se en besparelse.
      </p>
    </form>
  );
}

export function VarmeCalc() {
  const [bill, setBill] = useState("15000");
  const [gr, setGr] = useState("2");
  const b = num(bill);
  const g = num(gr);
  const s = b * 0.05 * g;

  return (
    <form className="meter" onSubmit={(e) => e.preventDefault()} aria-label="Beregn besparelse ved lavere temperatur">
      <div className="mgrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <label htmlFor="vm-bill">Varmeregning pr. år (kr.)</label>
          <input id="vm-bill" data-testid="calc-varme-bill-input" type="number" inputMode="decimal" min="0" value={bill} onChange={(e) => setBill(e.target.value)} />
        </div>
        <div>
          <label htmlFor="vm-gr">Grader lavere</label>
          <select id="vm-gr" data-testid="calc-varme-degrees-select" value={gr} onChange={(e) => setGr(e.target.value)}>
            {["1", "2", "3", "4"].map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>
      <dl className="stats" aria-live="polite" style={{ marginTop: "20px" }}>
        <div>
          <dt>Ny regning</dt>
          <dd data-testid="calc-varme-newbill-output">{fm(Math.max(0, b - s), 0)} kr.</dd>
        </div>
        <div>
          <dt>Pr. måned</dt>
          <dd data-testid="calc-varme-month-output">{fm(s / 12, 0)} kr.</dd>
        </div>
        <div className="big">
          <dt>Besparelse pr. år</dt>
          <dd data-testid="calc-varme-save-output">{fm(s, 0)} kr.</dd>
        </div>
      </dl>
      <p className="fine">Tommelfingerregel fra Bolius: cirka 5 % pr. grad. Gå ikke under 18 grader.</p>
    </form>
  );
}
